/**
 * Rotating and mirroring schematic elements (requirement 12.11).
 *
 * Presentation only (invariant 4): this changes where symbols are drawn and
 * which way they face, never a parameter, a port or a connection. That is also
 * why it is permitted on locked block members (D20): arranging is not editing.
 *
 * Conventions, shared with the scene builder and the renderer:
 *
 * - Screen coordinates, y down. Positive rotation is clockwise on screen.
 * - A symbol is mirrored left-right in its own frame first, then rotated. A
 *   vertical flip is a mirror plus a half turn, so one flag covers both.
 * - Stored rotations land exactly on quarter turns, so repeated operations
 *   never accumulate floating-point drift.
 *
 * Grouped operations turn and flip about the group's centre, as draw.io does:
 * the arrangement turns as a whole, and each symbol turns with it. A single
 * component turns in place. A block instance behaves as a group of its members,
 * and its collapsed symbol turns with them, so collapsed and expanded views
 * agree about which way everything faces.
 */
import { turnByQuarters } from "../design/angles";
import type {
  DesignDocument,
  NodeDocument,
  SchematicOrientation,
  SchematicPosition,
} from "../design/document";
import type { Point, SceneLibrary } from "../scene/types";

export type OrientOperation =
  | { kind: "rotate"; quarterTurns: number }
  /**
   * Horizontal mirrors left-right (about a vertical axis); vertical mirrors
   * top-bottom. Both in screen terms, whatever the element's current rotation.
   */
  | { kind: "flip"; axis: "horizontal" | "vertical" };

export interface Orientation {
  rotation: number;
  mirrored: boolean;
}

/**
 * The orientation after applying `operation` in screen terms.
 *
 * Mirroring a rotated symbol reverses its rotation, because a mirror M and a
 * rotation R satisfy M·R(θ) = R(−θ)·M. A vertical mirror is R(π)·M, so it
 * yields a rotation of π − θ.
 */
export function orient(current: Orientation, operation: OrientOperation): Orientation {
  if (operation.kind === "rotate") {
    return {
      rotation: turnByQuarters(current.rotation, operation.quarterTurns),
      mirrored: current.mirrored,
    };
  }
  return {
    rotation: turnByQuarters(-current.rotation, operation.axis === "vertical" ? 2 : 0),
    mirrored: !current.mirrored,
  };
}

/** An offset from the pivot after applying `operation`. Exact for quarter turns. */
export function orientOffset(offset: Point, operation: OrientOperation): Point {
  if (operation.kind === "flip") {
    return operation.axis === "horizontal"
      ? { x: -offset.x, y: offset.y }
      : { x: offset.x, y: -offset.y };
  }
  const quarters = ((operation.quarterTurns % 4) + 4) % 4;
  let { x, y } = offset;
  for (let i = 0; i < quarters; i += 1) {
    // A quarter clockwise on screen: east (1, 0) goes to south (0, 1).
    [x, y] = [-y, x];
  }
  // Normalise -0, so a turned point compares equal to one written by hand.
  return { x: x + 0, y: y + 0 };
}

function orientationOf(position: SchematicPosition | SchematicOrientation | null | undefined): Orientation {
  return { rotation: position?.rotation ?? 0, mirrored: position?.mirrored ?? false };
}

/**
 * Half the drawn box of a node, turned with it. Zero without a library, which
 * makes the group's pivot the centre of its centre points.
 */
function halfExtent(
  node: NodeDocument,
  position: SchematicPosition,
  library: SceneLibrary | undefined,
): { width: number; height: number } {
  if (!library) return { width: 0, height: 0 };
  const symbol = node.template_version_id ? library.templates[node.template_version_id]?.symbol : undefined;
  const width = symbol?.width ?? FALLBACK_SIZE.width;
  const height = symbol?.height ?? FALLBACK_SIZE.height;
  // Quarter turns only swap the sides; mirroring does not change the extent.
  const sideways = Math.round((position.rotation ?? 0) / (Math.PI / 2)) % 2 !== 0;
  return sideways ? { width: height / 2, height: width / 2 } : { width: width / 2, height: height / 2 };
}

function snap(value: number, spacing: number | null): number {
  return spacing ? Math.round(value / spacing) * spacing : value;
}

/** Symbol size when the template is unknown, matching the scene's fallback. */
const FALLBACK_SIZE = { width: 48, height: 32 };

export interface OrientOptions {
  /**
   * Resolved templates, so the pivot of a grouped operation is the centre of
   * the symbols' drawn boxes rather than of their centre points. Needed for a
   * collapsed block, whose symbol sits at that centre and should turn about
   * it; without it, the pivot is the centre of the centre points.
   */
  library?: SceneLibrary;
  /**
   * Grid spacing when snapping is on. The pivot of a grouped operation is
   * snapped to the grid, so a group that sat on grid points stays on them.
   */
  snap?: number | null;
  /**
   * Where each collapsed block's symbol is drawn. A collapsed block placed
   * from its lines has no stored position; turning it fixes the one it has,
   * so the symbol turns where it is and stays there. A collapsed block on its
   * own turns about its symbol's centre.
   */
  collapsedCentres?: ReadonlyMap<string, Point>;
}

export interface OrientResult {
  document: DesignDocument;
  /** The nodes that were turned or flipped, including block members. */
  nodes: string[];
  /** The pivot used, or null when every node turned in place. */
  pivot: Point | null;
}

/**
 * Rotate or mirror the given nodes and block instances.
 *
 * Only the elements touched get new objects, which is what the scene builder's
 * per-element memo relies on. Refs that name neither a node nor a live block
 * instance are ignored; edges have no orientation of their own and follow
 * their ports.
 */
export function orientElements(
  design: DesignDocument,
  refs: Iterable<string>,
  operation: OrientOperation,
  options: OrientOptions = {},
): OrientResult {
  const selected = new Set(refs);
  const instances = (design.group_instances ?? []).filter(
    (instance) => !instance.detached && selected.has(instance.local_id),
  );
  const targets = new Set<string>();
  for (const node of design.nodes ?? []) {
    if (selected.has(node.local_id)) targets.add(node.local_id);
  }
  for (const instance of instances) {
    for (const member of instance.member_local_ids ?? []) targets.add(member);
  }
  const nodes = (design.nodes ?? []).filter((node) => targets.has(node.local_id));
  if (nodes.length === 0 && instances.length === 0) {
    return { document: design, nodes: [], pivot: null };
  }

  // One component on its own turns in place. Anything more — several
  // components, or a block — turns about the centre of the arrangement.
  const inPlace = nodes.length === 1 && instances.length === 0;
  let pivot: Point | null = null;
  const alone = instances.length === 1 && nodes.every((node) => instances[0]!.member_local_ids?.includes(node.local_id));
  const ownCentre = alone ? options.collapsedCentres?.get(instances[0]!.local_id) : undefined;
  if (ownCentre) {
    pivot = ownCentre;
  } else if (!inPlace) {
    const xs: number[] = [];
    const ys: number[] = [];
    for (const node of nodes) {
      const position = node.schematic_position;
      if (!position) continue;
      const half = halfExtent(node, position, options.library);
      xs.push(position.x - half.width, position.x + half.width);
      ys.push(position.y - half.height, position.y + half.height);
    }
    if (xs.length > 0) {
      pivot = {
        x: snap((Math.min(...xs) + Math.max(...xs)) / 2, options.snap ?? null),
        y: snap((Math.min(...ys) + Math.max(...ys)) / 2, options.snap ?? null),
      };
    }
  }

  const turnNode = (node: NodeDocument): NodeDocument => {
    const position = node.schematic_position ?? { x: 0, y: 0 };
    const next = orient(orientationOf(position), operation);
    let { x, y } = position;
    if (pivot !== null && node.schematic_position) {
      const moved = orientOffset({ x: x - pivot.x, y: y - pivot.y }, operation);
      x = pivot.x + moved.x;
      y = pivot.y + moved.y;
    }
    return { ...node, schematic_position: { ...position, x, y, rotation: next.rotation, mirrored: next.mirrored } };
  };

  const turnedInstances = new Set(instances.map((instance) => instance.local_id));
  // Bends of lines within the group turn with it, keeping their shape.
  const turnPoint = (point: Point): Point => {
    if (pivot === null) return point;
    const moved = orientOffset({ x: point.x - pivot.x, y: point.y - pivot.y }, operation);
    return { x: pivot.x + moved.x, y: pivot.y + moved.y };
  };
  let edges = design.edges;
  if (pivot !== null && design.edges) {
    let changed = false;
    const next = design.edges.map((edge) => {
      const ends = [edge.from_port.split(".")[0]!, edge.to_port.split(".")[0]!];
      if (!edge.schematic_waypoints?.length || !ends.every((end) => targets.has(end))) return edge;
      changed = true;
      return { ...edge, schematic_waypoints: edge.schematic_waypoints.map(turnPoint) };
    });
    if (changed) edges = next;
  }
  const document: DesignDocument = {
    ...design,
    ...(edges !== design.edges ? { edges } : {}),
    nodes: (design.nodes ?? []).map((node) => (targets.has(node.local_id) ? turnNode(node) : node)),
    ...(turnedInstances.size > 0
      ? {
          group_instances: (design.group_instances ?? []).map((instance) => {
            if (!turnedInstances.has(instance.local_id)) return instance;
            const next = orient(orientationOf(instance.schematic_orientation), operation);
            const centre = options.collapsedCentres?.get(instance.local_id) ?? instance.collapsed_position;
            return {
              ...instance,
              schematic_orientation: next,
              ...(centre ? { collapsed_position: turnPoint(centre) } : {}),
            };
          }),
        }
      : {}),
  };
  return { document, nodes: nodes.map((node) => node.local_id), pivot };
}
